import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./AIContentPolicy.module.css";

const splitSentences = (text: string): string[] => {
  const matches =
    text.match(/[^.!?。！？؟।]+[.!?。！？؟।]+|[^.!?。！？؟।]+$/g) ?? [text];
  return matches.map((line) => line.trim()).filter(Boolean);
};

type PolicyTextProps = {
  children: string;
};

const PolicyText: React.FC<PolicyTextProps> = ({ children }) => (
  <p className={styles.text}>
    {splitSentences(children).map((line, index) => (
      <span key={`${index}-${line}`} className={styles.sentenceLine}>
        {line}
      </span>
    ))}
  </p>
);

const AIContentPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isKorean = (i18n.language ?? "en").toLowerCase().startsWith("ko");

  const title = t("ai_content_policy_page.title", {
    defaultValue: isKorean ? "AI 콘텐츠 이용 정책" : "AI Content Policy",
  });
  const intro = t("ai_content_policy_page.intro", {
    defaultValue: isKorean
      ? "AUTOVID는 사용자가 직접 보유하거나 사용할 권리가 있는 자료를 바탕으로 영상 제작 흐름을 구성하는 셀프서비스 소프트웨어입니다. AI 기능은 대본 초안, 장면용 시각 자료, 자막, 썸네일 등 제작 보조 용도로 제공됩니다."
      : "AUTOVID is self-serve software for building video production workflows from topics, source text, and materials that users own or have rights to use. AI features support script drafts, scene visuals, subtitles, thumbnails, and related production tasks.",
  });

  const permittedTitle = t("ai_content_policy_page.permitted_title", {
    defaultValue: isKorean ? "허용되는 사용" : "Permitted Use",
  });
  const prohibitedTitle = t("ai_content_policy_page.prohibited_title", {
    defaultValue: isKorean ? "금지되는 사용" : "Prohibited Use",
  });
  const responsibilityTitle = t("ai_content_policy_page.responsibility_title", {
    defaultValue: isKorean ? "사용자 책임" : "User Responsibility",
  });
  const aiInfraTitle = t("ai_content_policy_page.ai_infra_title", {
    defaultValue: isKorean ? "제3자 AI 인프라" : "Third-Party AI Infrastructure",
  });
  const safeguardsTitle = t("ai_content_policy_page.safeguards_title", {
    defaultValue: isKorean ? "안전장치 및 신고 검토" : "Safeguards and Abuse Review",
  });
  const generationSafetyTitle = t("ai_content_policy_page.generation_safety_title", {
    defaultValue: isKorean ? "NSFW 및 생성 안전장치" : "NSFW and Generation Safety",
  });
  const publishingTitle = t("ai_content_policy_page.publishing_title", {
    defaultValue: isKorean ? "자동 게시 금지" : "No Autonomous Publishing",
  });
  const disclosureTitle = t("ai_content_policy_page.disclosure_title", {
    defaultValue: isKorean ? "AI 콘텐츠 표시" : "AI Content Disclosure",
  });

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{title}</h1>
      <PolicyText>{intro}</PolicyText>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{permittedTitle}</h2>
        <ul className={styles.list}>
          <li>
            {t("ai_content_policy_page.permitted.item1", {
              defaultValue: isKorean
                ? "본인이 작성했거나 사용 권한을 가진 주제, 원문, 이미지, 오디오, 브랜드 자료를 활용한 영상 제작"
                : "Creating videos from topics, source text, images, audio, and brand materials that you created or are authorized to use",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.permitted.item2", {
              defaultValue: isKorean
                ? "배경, 제품 장면, 정보형 B-roll, 썸네일 배경 등 권리 침해 위험이 낮은 시각 자료 생성"
                : "Generating visual assets such as backgrounds, product scenes, informational B-roll, and thumbnail backgrounds",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.permitted.item3", {
              defaultValue: isKorean
                ? "사용자가 직접 검토하고 수정한 뒤 본인 채널 또는 본인이 관리하는 배포처에 게시"
                : "Reviewing and editing outputs before publishing them to channels or destinations you control",
            })}
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{prohibitedTitle}</h2>
        <ul className={styles.list}>
          <li>
            {t("ai_content_policy_page.prohibited.item1", {
              defaultValue: isKorean
                ? "공인, 사인 또는 식별 가능한 개인을 포함한 실존 인물을 사칭하는 콘텐츠 생성은 허용하지 않습니다."
                : "We do not allow the generation of content that impersonates real individuals, including public figures, private persons, or identifiable individuals.",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.prohibited.item2", {
              defaultValue: isKorean
                ? "AUTOVID는 실존 인물의 likeness를 재현하기 위한 사전 학습된 신원 모델, 얼굴 데이터셋 또는 전용 도구를 제공하지 않습니다."
                : "AUTOVID does not provide pre-trained identity models, facial datasets, or tools designed to recreate the likeness of real individuals.",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.prohibited.item3", {
              defaultValue: isKorean
                ? "딥페이크, 얼굴 교체, 타인의 초상 또는 유명인 likeness를 무단으로 생성하거나 사용하는 행위"
                : "Creating or using deepfakes, face swaps, or another person's likeness without authorization",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.prohibited.item4", {
              defaultValue: isKorean
                ? "타인의 목소리 사칭, 신원 사칭, 기만적 표현 또는 허위 보증처럼 보이는 콘텐츠 제작"
                : "Voice impersonation, identity impersonation, deceptive representations, or fake endorsements",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.prohibited.item5", {
              defaultValue: isKorean
                ? "저작권, 상표권, 초상권, 퍼블리시티권, 플랫폼 약관을 침해하는 자료의 입력 또는 결과물 배포"
                : "Uploading or distributing materials that infringe copyright, trademark, privacy, publicity, or platform rules",
            })}
          </li>
          <li>
            {t("ai_content_policy_page.prohibited.item6", {
              defaultValue: isKorean
                ? "성인물, 혐오, 폭력 조장, 불법 행위, 사기, 권리 침해 목적의 콘텐츠 제작"
                : "Creating adult, hateful, violent, illegal, fraudulent, or rights-infringing content",
            })}
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{publishingTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.publishing", {
            defaultValue: isKorean
              ? "AUTOVID는 생성된 콘텐츠를 자동으로 게시하거나 배포하지 않습니다. 모든 결과물은 사용자의 검토와 명시적인 사용자 조작 이후에만 게시 또는 배포될 수 있습니다."
              : "AUTOVID does not automatically publish or distribute generated content. All outputs require user review and explicit user action before publishing or distribution.",
          })}
        </PolicyText>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{disclosureTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.disclosure", {
            defaultValue: isKorean
              ? "출력 영상에 AI로 생성된 콘텐츠 또는 AI 보조 콘텐츠가 포함되는 경우, AI 콘텐츠가 포함되었음을 알리는 문구가 표시되도록 운영합니다."
              : "When an exported video includes AI-generated or AI-assisted content, AUTOVID applies a disclosure indicating that the video contains AI-generated content.",
          })}
        </PolicyText>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{responsibilityTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.responsibility", {
            defaultValue: isKorean
              ? "사용자는 입력 자료와 결과물에 필요한 권리와 라이선스를 확보해야 하며, 게시 전 결과물을 직접 검토해야 합니다. DREAMCRAFT는 금지된 사용을 발견하는 경우 계정 제한, 콘텐츠 삭제 요청, 환불 제한 등 필요한 조치를 취할 수 있습니다."
              : "Users are responsible for securing the rights and licenses required for their inputs and outputs, and must review outputs before publication. DREAMCRAFT may restrict accounts, request removal, or limit refunds when prohibited use is detected.",
          })}
        </PolicyText>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{aiInfraTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.ai_infra", {
            defaultValue: isKorean
              ? "AUTOVID의 AI 보조 기능은 Replicate, OpenAI, Google Gemini 및 유사한 제3자 AI 인프라, 모델 또는 API 제공업체와의 연동을 통해 제공될 수 있습니다. 이러한 기능은 사용자가 직접 입력하고 검토하는 제작 보조 도구이며, DREAMCRAFT가 사용자를 대신해 최종 콘텐츠의 주제 선정, 편집 판단, 게시 여부 또는 게시 채널 운영을 결정하지 않습니다. 별도로 명시하지 않는 한 AUTOVID 및 DREAMCRAFT는 해당 제3자 AI 인프라, 모델 또는 API 제공업체와 공식 제휴, 보증, 후원 또는 대리 관계에 있지 않습니다."
              : "AUTOVID's AI-assisted features may be powered by third-party AI infrastructure, model, or API providers, including Replicate, OpenAI, Google Gemini, and similar providers. These features are user-directed production tools; DREAMCRAFT does not decide final content topics, editing decisions, publication status, or publishing channel operations on behalf of users. Unless otherwise stated, AUTOVID and DREAMCRAFT are not officially affiliated with, endorsed by, sponsored by, or acting as agents of those third-party AI infrastructure, model, or API providers.",
          })}
        </PolicyText>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{generationSafetyTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.generation_safety", {
            defaultValue: isKorean
              ? "대본 생성 및 이미지 생성 과정에는 NSFW에 해당할 수 있는 성적, 폭력적, 혐오적, 불법 조장, 기타 플랫폼 정책상 민감할 수 있는 내용을 줄이기 위한 안전장치가 포함되어 있습니다."
              : "Script generation and image generation include safeguards intended to reduce potentially NSFW, sexual, violent, hateful, illegal, or otherwise platform-sensitive content.",
          })}
        </PolicyText>
      </section>

      <section className={styles.section}>
        <h2 className={styles.subTitle}>{safeguardsTitle}</h2>
        <PolicyText>
          {t("ai_content_policy_page.safeguards", {
            defaultValue: isKorean
              ? "DREAMCRAFT는 대본 생성 및 이미지 생성 과정에서 NSFW에 해당할 수 있는 성적, 폭력적, 혐오적, 불법 조장, 기타 플랫폼 정책상 민감할 수 있는 내용을 줄이기 위한 안전장치를 적용합니다. 또한 제3자 AI 제공업체의 안전 설정, 사용자 확인 절차, 신고 검토 절차를 운영할 수 있습니다. 금지된 사용이 확인되거나 신고되는 경우 접근 제한, 계정 정지, 서비스 제공 거절 등의 조치를 취할 수 있습니다."
              : "DREAMCRAFT applies safeguards during script generation and image generation to reduce potentially NSFW, sexual, violent, hateful, illegal, or otherwise platform-sensitive content. We may also use provider-level safety settings, user confirmations, and abuse review procedures. We may restrict access, suspend accounts, or deny service when prohibited use is detected or reported.",
          })}
        </PolicyText>
      </section>
    </div>
  );
};

export default AIContentPolicy;
